import {
  AfterViewInit,
  Component,
  effect,
  inject,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  INITIAL_STATE_TOKEN,
  ISelectFormElement,
  ISelectFormOption,
  TableRowSelectionComponent,
  tableRowSelectionImports,
} from 'frontier';
import AppControlGuids from '../../../api/ControlGuids';
import {Observable} from 'rxjs';
import {CartListService, TCart} from '../../../api/generated';
import {patchState} from '@ngrx/signals';
import {finalize, tap} from 'rxjs/operators';
import {ProcessOverviewStore} from '../process-overview/service/process-overview.store';
import {ICart} from '../process-overview/service/state.interface';

@Component({
  selector: 'app-cart-filter-selection',
  standalone: true,
  imports: tableRowSelectionImports,
  templateUrl: '../../../../../projects/frontier/browserkit/src/lib/components/control/table-row-selection/table-row-selection.component.html',
  styleUrl: '../../../../../projects/frontier/browserkit/src/lib/components/control/table-row-selection/table-row-selection.component.scss',
  providers: [
    {provide: INITIAL_STATE_TOKEN, useValue: {filter: null}}
  ],
})
export class CartFilterSelectionComponent extends TableRowSelectionComponent implements AfterViewInit {
  private _cartListService: CartListService = inject(CartListService);
  private _processOverviewStore: ProcessOverviewStore = inject(ProcessOverviewStore);

  private _selectedCart: Signal<ICart> = this._processOverviewStore.selectedCart;
  private _optionsVersion: WritableSignal<number> = signal(0);

  override GUID: string = AppControlGuids.TCartList;

  override selectionData: ISelectFormElement = {
    label: 'Fahrt',
    createNewEnabled: false,
    deleteElementEnabled: false,
    editElementEnabled: false,
    emptyOptionAdded: false,
  };

  override compareBy: (e1: TCart, e2: TCart) => boolean = (e1: TCart, e2: TCart): boolean => {
    return e1 && e2 && e1.signature === e2.signature;
  };

  override getDisplayValue: (row: TCart) => string = (row: TCart): string => {
    return row.name;
  }

  constructor() {
    super();

    effect((): void => {
      const selectedCart: ICart = this._selectedCart();
      const hasOptions: boolean = this._optionsVersion() > 0;
      if (!hasOptions || !selectedCart) {
        return;
      }

      const respectiveOption: ISelectFormOption = this.selectionData.options
        .find((option: ISelectFormOption): boolean => option.value?.signature === selectedCart?.obj?.signature);
      const currentSelected: TCart = this.formControlElement.value;

      if (respectiveOption && currentSelected?.signature !== respectiveOption.value.signature) {
        this.formControlElement.patchValue(respectiveOption.value);
      }
    });
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    // Update process overview store on form change
    this.formControlElement.valueChanges.subscribe((res: TCart): void => {
      this._processOverviewStore.selectedCart$.next(res ? {
        obj: res,
        number: res.name,
      } : null);
    });
  }

  override fetchRows(): Observable<object[]> {
    patchState(this.state, {loading: true});

    return this._cartListService.cartListGetCartsForMissionData({
      InstanceId: this.instanceId(),
    }).pipe(
      tap((carts: TCart[]): void => {
        this.setData(carts);
        this.selectionDataChanged.emit(null);
      }),
      finalize((): void => {
          patchState(this.state, {loading: false});
        }
      ));
  }

  setData(carts: TCart[]): void {
    this.selectionData = {
      ...this.selectionData,
      options: carts.map((cart: TCart): ISelectFormOption => {
        return {
          name: this.getDisplayValue(cart),
          value: cart,
        }
      }),
      multiple: this.multiple,
    }
    this._optionsVersion.update((v: number): number => v + 1);

    const selectedCart: ICart = this._selectedCart();
    if (selectedCart) {
      const matchingCart: TCart = carts.find(
        (c: TCart): boolean => c.signature === selectedCart.obj?.signature
      );
      if (matchingCart && this.formControlElement?.value?.signature !== matchingCart.signature) {
        this.formControlElement.patchValue(matchingCart);
      }
    }

    this.cdr.detectChanges();
  }
}
